import {AreaNode, DepartmentNode, EquipmentNode, ProductionLineNode} from '@hconnect/common/types'
import {useUserContext} from '@hconnect/login'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate, useSearchParams} from 'react-router'

import {PermissionParsed, usePermissionsQuery} from '../../permissions'
import {getUrl, PLANT_ROUTES, useUrlParam} from '../../routing'
import {
  tabToCustomFilterMap,
  tabToPermissionsMap,
  tabToUserIdsAllowed,
  upmNodeToTabsMap
} from '../plant-setup-tabs/tabs.config'

import {useSelectedItem} from './SelectedItemProvider'

import {HeaderTab} from '@settings/modules/common/components/HeaderTabs'
import {useLatestQuery} from '@settings/modules/common/hooks'
import {Latest} from '@settings/modules/common/types/latest'
import {checkMultiplePermissions} from '@settings/modules/permissions/helpers/checkMultiplePermissions'
import {checkPermissionAccessByDataScope} from '@settings/modules/permissions/helpers/checkPermissionAccessByDataScope'

interface ItemDetailsState {
  availableTabs: HeaderTab[]
  selectedTab: HeaderTab | undefined
  handleTabChange: (tab: HeaderTab) => void
  userPermissions: PermissionParsed[] | undefined
  latestData: Latest | undefined
  departmentToDelete?: DepartmentNode
  departmentToDeleteParentUpmId?: string
  setDepartmentToDelete: (department?: DepartmentNode, parentUpmId?: string) => void
  equipmentToDelete?: EquipmentNode
  setEquipmentToDelete: (equipment?: EquipmentNode) => void
  editedArea: AreaNode | null
  setEditedArea: (area: AreaNode | null) => void
  addedArea: AreaNode | null
  setAddArea: (area: AreaNode | null) => void
  addedProductionLine: ProductionLineNode | undefined
  setAddProductionLine: (productionLine: ProductionLineNode | undefined) => void
  departmentToRestore?: DepartmentNode
  setDepartmentToRestore: (item: DepartmentNode | undefined) => void
  equipmentToRestore?: EquipmentNode
  setEquipmentToRestore: (item: EquipmentNode | undefined) => void
}

const ItemDetailsContext = React.createContext<ItemDetailsState | undefined>(undefined)

export const useItemDetails = () => {
  const context = useContext(ItemDetailsContext)
  if (!context) throw new Error('Cannot use selected item context outside of a context provider')
  return context
}

export const ItemDetailsProvider = ({children}: {children: React.ReactNode}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const plantCode = useUrlParam('plantCode')
  const [addedArea, setAddArea] = useState<AreaNode | null>(null)
  const [editedArea, setEditedArea] = useState<AreaNode | null>(null)
  const [departmentToDelete, setDepartmentToDeleteState] = useState<DepartmentNode | undefined>()
  const [departmentToDeleteParentUpmId, setDepartmentToDeleteParentUpmId] = useState<
    string | undefined
  >()
  const [equipmentToDelete, setEquipmentToDelete] = useState<EquipmentNode | undefined>()

  const setDepartmentToDelete = (department?: DepartmentNode, parentUpmId?: string) => {
    setDepartmentToDeleteState(department)
    setDepartmentToDeleteParentUpmId(parentUpmId)
  }
  const [addedProductionLine, setAddProductionLine] = useState<ProductionLineNode | undefined>()
  const [departmentToRestore, setDepartmentToRestore] = useState<DepartmentNode | undefined>()
  const [equipmentToRestore, setEquipmentToRestore] = useState<EquipmentNode | undefined>()

  const {loggedUser} = useUserContext()
  const userId = loggedUser?.user_id

  const {data: userPermissions} = usePermissionsQuery()
  const {data: latestData} = useLatestQuery()

  const {selectedItem} = useSelectedItem()

  const availableTabs: HeaderTab[] = useMemo(() => {
    if (!selectedItem || !userPermissions || !latestData) return []

    const tabsForSelectedNodeType = upmNodeToTabsMap[selectedItem.itemType]

    const tabsFilteredByPermission = tabsForSelectedNodeType.filter((tab) => {
      const requiredPermissions = tabToPermissionsMap[tab]
      if (requiredPermissions.length)
        checkMultiplePermissions({
          permissions: userPermissions,
          requiredPermissionTypes: requiredPermissions,
          testFn: (permission) =>
            checkPermissionAccessByDataScope(
              latestData.country,
              plantCode,
              permission.parsedDataScope
            )
        })

      return true
    })

    const tabsFilteredByCustomFilter = tabsFilteredByPermission.filter((tab) =>
      tabToCustomFilterMap[tab](selectedItem)
    )

    // temporary solution for updating department types
    // HCP-94082
    const tabsFilteredbyUserId = tabsFilteredByCustomFilter.filter((tab) =>
      tabToUserIdsAllowed[tab](userId || '')
    )

    return tabsFilteredbyUserId.map((tab) => {
      return {
        value: tab,
        label: t(`plantSetup.${tab}`)
      }
    })
  }, [selectedItem, userPermissions, latestData, plantCode, userId, t])

  const [selectedTab, setSelectedTab] = useState<HeaderTab | undefined>(availableTabs[0])

  const memoizedSelectedTab = useMemo(() => {
    if (!selectedTab || !availableTabs.find(({value}) => value === selectedTab.value)) {
      return availableTabs[0]
    }
    return selectedTab
  }, [selectedTab, availableTabs])

  const handleTabChange = useCallback(
    (tab: HeaderTab) => {
      if (!selectedItem) return
      setSelectedTab(tab)
      const targetUrl = getUrl(PLANT_ROUTES.PLANT_SETUP.NODE.NODE_TAB.path, {
        plantCode,
        nodeId: selectedItem.upmId,
        nodeTab: tab.value
      })
      const searchParamsString = searchParams.toString()
      void navigate(searchParamsString ? `${targetUrl}?${searchParamsString}` : targetUrl)
    },
    [plantCode, selectedItem, navigate, searchParams]
  )

  return (
    <ItemDetailsContext.Provider
      value={{
        availableTabs,
        selectedTab: memoizedSelectedTab,
        handleTabChange,
        userPermissions,
        latestData,
        editedArea,
        setEditedArea,
        departmentToDelete,
        departmentToDeleteParentUpmId,
        setDepartmentToDelete,
        equipmentToDelete,
        setEquipmentToDelete,
        addedArea,
        setAddArea,
        addedProductionLine,
        setAddProductionLine,
        departmentToRestore,
        setDepartmentToRestore,
        equipmentToRestore,
        setEquipmentToRestore
      }}
    >
      {children}
    </ItemDetailsContext.Provider>
  )
}
